import { createConsumer, type Consumer } from '@rails/actioncable'
import { isNative } from '@/platform'
import { apiBaseUrl } from '@/platform/config'

let consumer: Consumer | null = null

/**
 * Resolve the WebSocket URL for ActionCable.
 *
 * - Web: same-origin — `ws(s)://<host>/cable`, driven by the current page.
 * - Native: the Capacitor WebView is served from `https://localhost/` (or
 *   similar), so `window.location.host` points at the bundle, not the API.
 *   We rebase onto `apiBaseUrl()` (from `VITE_API_BASE_URL`), swapping the
 *   HTTP scheme for WebSocket.
 *
 * Exported for test coverage of the branch logic.
 */
export function buildCableUrl(token: string): string {
  const encodedToken = encodeURIComponent(token)
  if (isNative()) {
    const base = apiBaseUrl()
    if (base) {
      const wsBase = base.replace(/^http/i, 'ws') // https → wss, http → ws
      return `${wsBase.replace(/\/$/, '')}/cable?token=${encodedToken}`
    }
    // Fall through to same-origin if no VITE_API_BASE_URL was baked in —
    // keeps local cap dev against a Rails server on localhost usable.
  }
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
  return `${protocol}//${window.location.host}/cable?token=${encodedToken}`
}

export function getConsumer(token: string): Consumer {
  if (consumer) return consumer
  consumer = createConsumer(buildCableUrl(token))
  return consumer
}

export function disconnectConsumer(): void {
  if (consumer) {
    consumer.disconnect()
    consumer = null
  }
}
